import { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Users, Bell, MapPin, Target, Video, MessageSquare, FileText, Image, Layout, Settings, Inbox, Menu, X, Award, ListChecks, Calendar } from 'lucide-react';
import PresetTasksManagement from './PresetTasksManagement';
import NotificationManagement from './NotificationManagement';
import ClassScheduleManagement from './ClassScheduleManagement';

export default function AdminDashboard({ user, setUser }) {
  // Always default to 'users' tab for both super_admin and org_admin
  const [activeTab, setActiveTab] = useState('users');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    // Redirect based on user role
    if (user.role === 'org_admin') {
      navigate('/org-admin/login');
    } else {
      navigate('/admin/login');
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} md:w-64 bg-slate-800 text-white flex flex-col transition-all duration-300 fixed md:relative h-full z-30 overflow-hidden`}>
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">後台管理</h2>
            <p className="text-sm text-slate-400 mt-1">系統管理</p>
          </div>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="md:hidden p-2 hover:bg-slate-700 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {user.role === 'super_admin' && (
            <TabButton icon={Building2} label="機構管理" active={activeTab === 'organizations'} onClick={() => {
              setActiveTab('organizations');
              if (window.innerWidth < 768) setSidebarOpen(false);
            }} />
          )}
          <TabButton icon={Users} label="用戶管理" active={activeTab === 'users'} onClick={() => {
            setActiveTab('users');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Users} label="教練管理" active={activeTab === 'coaches'} onClick={() => {
            setActiveTab('coaches');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Inbox} label="收件夾" active={activeTab === 'inbox'} onClick={() => {
            setActiveTab('inbox');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Layout} label="頁面配置" active={activeTab === 'page-config'} onClick={() => {
            setActiveTab('page-config');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Bell} label="公告管理" active={activeTab === 'announcements'} onClick={() => {
            setActiveTab('announcements');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Bell} label="推送通知" active={activeTab === 'push-notifications'} onClick={() => {
            setActiveTab('push-notifications');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={MapPin} label="地點管理" active={activeTab === 'locations'} onClick={() => {
            setActiveTab('locations');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Award} label="獎項配置" active={activeTab === 'award-config'} onClick={() => {
            setActiveTab('award-config');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Target} label="任務管理" active={activeTab === 'tasks'} onClick={() => {
            setActiveTab('tasks');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={ListChecks} label="預設任務庫" active={activeTab === 'preset-tasks'} onClick={() => {
            setActiveTab('preset-tasks');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Calendar} label="課時紀錄" active={activeTab === 'class-schedule'} onClick={() => {
            setActiveTab('class-schedule');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Video} label="影片管理" active={activeTab === 'videos'} onClick={() => {
            setActiveTab('videos');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={MessageSquare} label="提問管理" active={activeTab === 'questions'} onClick={() => {
            setActiveTab('questions');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={FileText} label="內容頁面" active={activeTab === 'pages'} onClick={() => {
            setActiveTab('pages');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          <TabButton icon={Image} label="媒體庫" active={activeTab === 'media'} onClick={() => {
            setActiveTab('media');
            if (window.innerWidth < 768) setSidebarOpen(false);
          }} />
          {user.role === 'super_admin' && (
            <>
              <TabButton icon={Settings} label="超管設定" active={activeTab === 'super-settings'} onClick={() => {
                setActiveTab('super-settings');
                if (window.innerWidth < 768) setSidebarOpen(false);
              }} />
              <TabButton icon={Target} label="系統優化" active={activeTab === 'optimization'} onClick={() => {
                setActiveTab('optimization');
                if (window.innerWidth < 768) setSidebarOpen(false);
              }} />
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <Button onClick={handleLogout} className="w-full bg-red-600 hover:bg-red-700">登出</Button>
        </div>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Mobile menu button - Top left corner */}
        {!sidebarOpen && (
          <div className="md:hidden fixed top-4 left-4 z-20">
            <button
              onClick={() => setSidebarOpen(true)}
              className="bg-slate-800 text-white p-3 rounded-lg shadow-lg hover:bg-slate-700 transition-colors"
              aria-label="開啟選單"
            >
              <Menu size={20} />
            </button>
          </div>
        )}
        
        {/* Content with proper padding to avoid overlap */}
        <div className={`p-4 md:p-8 ${!sidebarOpen ? 'pt-16 md:pt-8' : ''}`}>
          {activeTab === 'organizations' && user.role === 'super_admin' && <OrganizationsTab user={user} />}
          {activeTab === 'users' && <UsersTab user={user} />}
          {activeTab === 'coaches' && <CoachesTab user={user} />}
          {activeTab === 'inbox' && <InboxTab user={user} />}
          {activeTab === 'page-config' && <PageConfigTab user={user} />}
          {activeTab === 'announcements' && <AnnouncementsTab />}
          {activeTab === 'push-notifications' && <NotificationManagement />}
          {activeTab === 'locations' && <LocationsTab />}
          {activeTab === 'award-config' && <AwardConfigTab user={user} />}
          {activeTab === 'tasks' && <TasksTab />}
          {activeTab === 'preset-tasks' && <PresetTasksManagement />}
          {activeTab === 'class-schedule' && <ClassScheduleManagement user={user} />}
          {activeTab === 'videos' && <VideosTab />}
          {activeTab === 'questions' && <QuestionsTab />}
          {activeTab === 'pages' && <PagesTab />}
          {activeTab === 'media' && <MediaTab />}
          {activeTab === 'super-settings' && user.role === 'super_admin' && <SuperAdminSettingsTab user={user} />}
          {activeTab === 'optimization' && user.role === 'super_admin' && <OptimizationTab />}
        </div>
      </div>
    </div>
  );
}

function TabButton({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-sm md:text-base ${
        active ? 'bg-slate-700' : 'hover:bg-slate-700/50'
      }`}
    >
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );
}

function OrganizationsTab({ user }) {
  const [organizations, setOrganizations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', logo_url: '' });
  const [editIdDialogOpen, setEditIdDialogOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newOrgId, setNewOrgId] = useState('');
  const [adminDetailsDialogOpen, setAdminDetailsDialogOpen] = useState(false);
  const [orgAdmins, setOrgAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [customDomain, setCustomDomain] = useState('');

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/organizations`);
      setOrganizations(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const loadOrgAdmins = async (orgId) => {
    setLoadingAdmins(true);
    try {
      const res = await axios.get(`${API}/users-with-passwords?organization_id=${orgId}`);
      // Filter only org_admin role
      const admins = res.data.filter(user => user.role === 'org_admin');
      setOrgAdmins(admins);
    } catch (error) {
      toast.error('載入管理員資料失敗');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleViewAdmins = (org) => {
    setSelectedOrg(org);
    loadOrgAdmins(org.organization_id);
    setAdminDetailsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations`, formData);
      toast.success('機構已新增');
      setDialogOpen(false);
      setFormData({ name: '', logo_url: '' });
      loadOrganizations();
    } catch (error) {
      toast.error('新增失敗');
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`確定要刪除機構 "${org.name}" 嗎？\n\n這將刪除該機構的所有數據，包括：\n- 所有用戶\n- 所有內容\n- 所有設定\n\n此操作無法撤銷！`)) {
      return;
    }

    try {
      await axios.delete(`${API}/organizations/${org.organization_id}`);
      toast.success('機構已刪除');
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || '刪除失敗');
    }
  };

  const handleChangeOrgId = async () => {
    if (!newOrgId) {
      toast.error('請輸入新的機構ID');
      return;
    }

    if (!window.confirm(`確定要將機構ID從 "${selectedOrg.organization_id}" 改為 "${newOrgId}" 嗎？\n\n這將更新所有相關數據。`)) {
      return;
    }

    try {
      await axios.put(`${API}/organizations/${selectedOrg.organization_id}/change-id`, {
        new_organization_id: newOrgId
      });
      toast.success('機構ID已更新');
      setEditIdDialogOpen(false);
      setNewOrgId('');
      setSelectedOrg(null);
      loadOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || '更新失敗');
    }
  };

  return (
    <div data-testid="organizations-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">機構管理</h1>
        {user?.role === 'super_admin' && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-org-btn">新增機構</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>新增機構</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  data-testid="org-name-input"
                  placeholder="機構名稱"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
                <Input
                  placeholder="Logo URL（選填）"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                />
                <Button data-testid="submit-org-btn" type="submit" className="w-full">新增</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organizations.map((org) => (
          <div 
            key={org.organization_id} 
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow" 
            data-testid="org-item"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">{org.name}</h3>
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-sm text-gray-600">ID: {org.organization_id}</p>
                  {user?.role === 'super_admin' && (
                    <button
                      onClick={() => {
                        setSelectedOrg(org);
                        setNewOrgId(org.organization_id);
                        setEditIdDialogOpen(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                      title="修改機構ID"
                    >
                      修改
                    </button>
                  )}
                </div>
                
                {/* Admin info button */}
                {user?.role === 'super_admin' && (
                  <button
                    onClick={() => handleViewAdmins(org)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 mt-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    查看管理員
                  </button>
                )}
                
                {org.theme_color && (
                  <div className="flex items-center gap-2 mt-2">
                    <div 
                      className="w-6 h-6 rounded border" 
                      style={{ backgroundColor: org.theme_color }}
                    />
                    <span className="text-xs text-gray-500">主題色</span>
                  </div>
                )}
                
                {/* Custom Domain Display */}
                {org.custom_domain && (
                  <div className="flex items-center gap-2 mt-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="text-xs text-green-600 font-medium">{org.custom_domain}</span>
                  </div>
                )}
                
                {/* Organization Entry Settings */}
                {user?.role === 'super_admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">需要輸入機構ID</span>
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            await axios.put(`${API}/organizations/${org.organization_id}`, {
                              require_org_id: !(org.require_org_id !== false)
                            }, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            toast.success('設定已更新');
                            loadOrganizations();
                          } catch (error) {
                            toast.error('更新失敗');
                          }
                        }}
                        className={`w-10 h-5 rounded-full transition-colors ${org.require_org_id !== false ? 'bg-blue-600' : 'bg-gray-300'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform ${org.require_org_id !== false ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedOrg(org);
                        setDomainDialogOpen(true);
                      }}
                      className="text-xs text-purple-600 hover:text-purple-800 underline"
                    >
                      設定自訂網域
                    </button>
                  </div>
                )}
              </div>
              {org.logo_url && (
                <img src={org.logo_url} alt="Logo" className="h-12 w-12 object-contain" />
              )}
            </div>
            <div className="pt-3 border-t flex gap-2">
              <button
                onClick={() => window.location.href = `/org-settings/${org.organization_id}`}
                className="flex-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                進入設定 →
              </button>
              {user?.role === 'super_admin' && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(org)}
                >
                  刪除
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Organization ID Dialog */}
      <Dialog open={editIdDialogOpen} onOpenChange={setEditIdDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>修改機構ID</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">當前ID</label>
              <Input 
                value={selectedOrg?.organization_id || ''} 
                disabled 
                className="bg-gray-50"
              />
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">新ID</label>
              <Input
                placeholder="輸入新的機構ID"
                value={newOrgId}
                onChange={(e) => setNewOrgId(e.target.value)}
              />
            </div>
            <p className="text-sm text-amber-600">⚠️ 修改機構ID將更新所有相關數據</p>
            <Button onClick={handleChangeOrgId} className="w-full">確認修改</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Details Dialog */}
      <Dialog open={adminDetailsDialogOpen} onOpenChange={setAdminDetailsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>機構管理員資料 - {selectedOrg?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingAdmins ? (
              <div className="text-center py-8 text-gray-500">載入中...</div>
            ) : orgAdmins.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="mb-2">此機構尚未創建管理員帳號</p>
                <p className="text-sm text-gray-400">機構ID: {selectedOrg?.organization_id}</p>
              </div>
            ) : (
              <div>
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">
                    共有 {orgAdmins.length} 個管理員帳號
                  </p>
                </div>
                <div className="space-y-3">
                  {orgAdmins.map((admin, index) => (
                    <div key={admin.user_id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">
                              管理員 #{index + 1}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">用戶名稱：</span>
                              <span className="font-medium">{admin.username}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">電郵：</span>
                              <span className="font-medium">{admin.email}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">電話：</span>
                              <span className="font-medium">{admin.phone}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">密碼：</span>
                              <code className="text-xs bg-white px-2 py-1 rounded border">
                                {admin.plain_password || 'N/A'}
                              </code>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-600">創建時間：</span>
                              <span className="font-medium">
                                {new Date(admin.created_at).toLocaleString('zh-TW')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Domain Dialog */}
      <Dialog open={domainDialogOpen} onOpenChange={(open) => {
        setDomainDialogOpen(open);
        if (open && selectedOrg) {
          setCustomDomain(selectedOrg.custom_domain || '');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>設定自訂網域</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-600 mb-1 block">機構名稱</label>
              <p className="font-medium">{selectedOrg?.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600 mb-1 block">自訂網域</label>
              <Input
                placeholder="例如：myschool.example.com"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                設定後，用戶訪問此網域時將自動跳轉到該組織的登入頁面
              </p>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>⚠️ 注意：</strong>您需要在 DNS 設定中將此網域指向我們的伺服器，才能使此功能生效。
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setCustomDomain('');
                }}
                className="flex-1"
              >
                清除網域
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(`${API}/organizations/${selectedOrg.organization_id}`, {
                      custom_domain: customDomain.trim() || null
                    }, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    toast.success('自訂網域已更新');
                    setDomainDialogOpen(false);
                    loadOrganizations();
                  } catch (error) {
                    toast.error('更新失敗');
                  }
                }}
                className="flex-1"
              >
                儲存
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UsersTab({ user: currentUser }) {
  const [users, setUsers] = useState([]);
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, approved, rejected
  const [organizations, setOrganizations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userDetailsDialogOpen, setUserDetailsDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [currentOrgSettings, setCurrentOrgSettings] = useState(null);
  
  // 編輯學生相關 state
  const [editStudentDialogOpen, setEditStudentDialogOpen] = useState(false);
  const [editingStudentData, setEditingStudentData] = useState(null);
  const [savingStudent, setSavingStudent] = useState(false);
  
  // 新增學生相關 state
  const [addStudentDialogOpen, setAddStudentDialogOpen] = useState(false);
  const [simpleMode, setSimpleMode] = useState(true); // 簡易模式開關
  const [batchMode, setBatchMode] = useState(false); // 批量模式開關
  const [batchNames, setBatchNames] = useState(''); // 批量學生姓名
  const [showStudentCreatedDialog, setShowStudentCreatedDialog] = useState(false);
  const [createdStudentInfo, setCreatedStudentInfo] = useState(null);
  const [batchCreatedStudents, setBatchCreatedStudents] = useState([]); // 批量創建結果
  const [coaches, setCoaches] = useState([]);
  const [newStudent, setNewStudent] = useState({
    organization_id: '',
    username: '',
    student_name: '',
    email: '',
    phone: '',
    password: '',
    coach_id: '',
    class_days: [],
    fee_per_class: '',
    time_slot: '10:00-11:00',
    location: '',
    student_id: ''
  });
  const [submittingStudent, setSubmittingStudent] = useState(false);

  useEffect(() => {
    loadUsers();
    loadOrganizations();
    loadCoaches();
  }, []);

  useEffect(() => {
    // Load current organization settings when filterOrg changes
    if (filterOrg && filterOrg !== 'all') {
      loadOrgSettings(filterOrg);
    }
  }, [filterOrg]);

  const loadCoaches = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/coaches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoaches(res.data);
    } catch (error) {
      console.error('Failed to load coaches');
    }
  };

  const handleCreateStudent = async (e) => {
    e.preventDefault();
    
    // 簡易模式：所有欄位都是可選的
    if (simpleMode) {
      // 不做任何驗證，所有欄位都可選
    } else {
      // 完整模式需要更多欄位
      if (!newStudent.organization_id || !newStudent.username || !newStudent.email || 
          !newStudent.password || !newStudent.coach_id) {
        toast.error('請填寫所有必填欄位');
        return;
      }
      if (newStudent.class_days.length === 0) {
        toast.error('請選擇至少一個上課日期');
        return;
      }
    }
    
    setSubmittingStudent(true);
    try {
      const token = localStorage.getItem('token');
      
      if (simpleMode) {
        // 簡易模式：調用新的簡易版 API
        const simpleData = {
          organization_id: newStudent.organization_id || '',
          student_name: newStudent.student_name,
          coach_id: newStudent.coach_id || '',
          class_days: newStudent.class_days,
          fee_per_class: newStudent.fee_per_class || 0,
          time_slot: newStudent.time_slot || '',
          location: newStudent.location || ''
        };
        
        const response = await axios.post(`${API}/admin/students/simple`, simpleData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // 顯示學生登入資訊
        setCreatedStudentInfo(response.data.student);
        setAddStudentDialogOpen(false);
        setShowStudentCreatedDialog(true);
      } else {
        // 完整模式：調用原有 API
        await axios.post(`${API}/admin/students`, newStudent, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('學生已新增，課程排程已自動創建');
        setAddStudentDialogOpen(false);
      }
      
      // 重置表單
      setNewStudent({
        organization_id: '',
        username: '',
        student_name: '',
        email: '',
        phone: '',
        password: '',
        coach_id: '',
        class_days: [],
        fee_per_class: 0,
        time_slot: '10:00-11:00',
        location: '',
        student_id: ''
      });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || '新增失敗');
    } finally {
      setSubmittingStudent(false);
    }
  };

  const toggleClassDay = (day) => {
    setNewStudent(prev => ({
      ...prev,
      class_days: prev.class_days.includes(day)
        ? prev.class_days.filter(d => d !== day)
        : [...prev.class_days, day].sort()
    }));
  };

  // 批量創建學生
  const handleBatchCreateStudents = async (e) => {
    e.preventDefault();
    
    // 解析學生姓名（按換行分隔）
    const names = batchNames
      .split('\n')
      .map(name => name.trim())
      .filter(name => name.length > 0);
    
    if (names.length === 0) {
      toast.error('請輸入至少一個學生姓名');
      return;
    }
    
    setSubmittingStudent(true);
    const results = [];
    const token = localStorage.getItem('token');
    
    try {
      for (const name of names) {
        try {
          const simpleData = {
            organization_id: newStudent.organization_id || '',
            student_name: name,
            coach_id: newStudent.coach_id || '',
            class_days: newStudent.class_days,
            fee_per_class: parseFloat(newStudent.fee_per_class) || 0,
            time_slot: newStudent.time_slot || '',
            location: newStudent.location || ''
          };
          
          const response = await axios.post(`${API}/admin/students/simple`, simpleData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          results.push({
            success: true,
            name: name,
            student: response.data.student
          });
        } catch (error) {
          results.push({
            success: false,
            name: name,
            error: error.response?.data?.detail || '創建失敗'
          });
        }
      }
      
      // 顯示批量創建結果
      setBatchCreatedStudents(results);
      setAddStudentDialogOpen(false);
      setShowStudentCreatedDialog(true);
      
      // 重置表單
      setBatchNames('');
      setNewStudent({
        organization_id: '',
        username: '',
        student_name: '',
        email: '',
        phone: '',
        password: '',
        coach_id: '',
        class_days: [],
        fee_per_class: '',
        time_slot: '10:00-11:00',
        location: '',
        student_id: ''
      });
      loadUsers();
      
      const successCount = results.filter(r => r.success).length;
      toast.success(`成功創建 ${successCount}/${names.length} 個學生`);
    } catch (error) {
      toast.error('批量創建失敗');
    } finally {
      setSubmittingStudent(false);
    }
  };

  const loadOrgSettings = async (orgId) => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}`);
      setCurrentOrgSettings(res.data);
    } catch (error) {
      console.error('Failed to load org settings');
    }
  };

  const handleToggleApprovalSetting = async () => {
    if (!currentOrgSettings || filterOrg === 'all') {
      toast.error('請先選擇一個機構');
      return;
    }

    try {
      const newValue = !currentOrgSettings.require_approval;
      await axios.put(`${API}/organizations/${filterOrg}`, {
        require_approval: newValue
      });
      
      setCurrentOrgSettings({
        ...currentOrgSettings,
        require_approval: newValue
      });
      
      toast.success(newValue ? '已開啟帳戶審批' : '已關閉帳戶審批');
    } catch (error) {
      toast.error('設置失敗');
    }
  };

  const loadOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/organizations`);
      setOrganizations(res.data);
    } catch (error) {
      console.error('Failed to load organizations');
    }
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/users-with-passwords`);
      setUsers(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const loadUserDetails = async (user) => {
    setLoadingDetails(true);
    setUserDetailsDialogOpen(true);
    setSelectedUser(user);
    
    try {
      // Load user's class records
      const recordsRes = await axios.get(`${API}/class-records?student_id=${user.user_id}`);
      
      // Load user's task completions
      const tasksRes = await axios.get(`${API}/daily-tasks/completions?student_id=${user.user_id}`);
      
      setUserDetails({
        ...user,
        classRecords: recordsRes.data || [],
        taskCompletions: tasksRes.data || []
      });
    } catch (error) {
      console.error('Failed to load user details', error);
      setUserDetails({
        ...user,
        classRecords: [],
        taskCompletions: []
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword) {
      toast.error('請輸入新密碼');
      return;
    }

    try {
      await axios.post(`${API}/users/${selectedUser.user_id}/change-password`, {
        new_password: newPassword
      });
      toast.success('密碼已更新');
      setPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
      loadUsers();  // Reload users to show updated password
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`確定要刪除用戶 "${user.username}" 嗎？此操作無法撤銷。`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${user.user_id}`);
      toast.success('用戶已刪除');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || '刪除失敗');
    }
  };

  const handleApproveUser = async (userId, status) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, { status });
      toast.success(status === 'approved' ? '已批准' : '已拒絕');
      loadUsers();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  // 開啟編輯學生對話框
  const handleEditStudent = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/students/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const studentData = response.data;
      
      setEditingStudentData({
        user_id: studentData.user_id,
        student_name: studentData.student_name || '',
        coach_id: studentData.coach_id || '',
        class_days: studentData.class_days || [],
        fee_per_class: studentData.fee_per_class || 0,
        monthly_fee: studentData.monthly_fee || 0,
        time_slot: studentData.time_slot || studentData.schedules?.[0]?.time_slot || '10:00-11:00',
        location: studentData.class_location || studentData.schedules?.[0]?.location || '',
        organization_id: studentData.organization_id
      });
      setEditStudentDialogOpen(true);
    } catch (error) {
      console.error('Failed to load student details:', error);
      // 如果 API 失敗，使用基本數據
      setEditingStudentData({
        user_id: user.user_id,
        student_name: user.student_name || '',
        coach_id: user.coach_id || '',
        class_days: [],
        fee_per_class: user.fee_per_class || 0,
        monthly_fee: user.monthly_fee || 0,
        time_slot: '10:00-11:00',
        location: user.class_location || '',
        organization_id: user.organization_id
      });
      setEditStudentDialogOpen(true);
    }
  };

  // 保存編輯的學生資料
  const handleSaveStudent = async () => {
    if (!editingStudentData) return;
    
    setSavingStudent(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/students/${editingStudentData.user_id}`, {
        student_name: editingStudentData.student_name,
        coach_id: editingStudentData.coach_id,
        class_days: editingStudentData.class_days,
        fee_per_class: parseFloat(editingStudentData.fee_per_class) || 0,
        monthly_fee: parseFloat(editingStudentData.monthly_fee) || 0,
        time_slot: editingStudentData.time_slot,
        location: editingStudentData.location
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('學生資料已更新');
      setEditStudentDialogOpen(false);
      setEditingStudentData(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to update student:', error);
      toast.error(error.response?.data?.detail || '更新失敗');
    } finally {
      setSavingStudent(false);
    }
  };

  // 切換編輯中的上課日期
  const toggleEditClassDay = (day) => {
    if (!editingStudentData) return;
    
    const currentDays = editingStudentData.class_days || [];
    let newDays;
    if (currentDays.includes(day)) {
      newDays = currentDays.filter(d => d !== day);
    } else {
      newDays = [...currentDays, day].sort((a, b) => a - b);
    }
    
    // 自動計算月費
    const feePerClass = parseFloat(editingStudentData.fee_per_class) || 0;
    const newMonthlyFee = newDays.length * feePerClass * 4;
    
    setEditingStudentData({
      ...editingStudentData,
      class_days: newDays,
      monthly_fee: newMonthlyFee
    });
  };

  const filteredUsers = users.filter(u => {
    if (filterOrg !== 'all' && u.organization_id !== filterOrg) return false;
    if (filterStatus !== 'all' && u.account_status !== filterStatus) return false;
    return true;
  });

  const pendingCount = users.filter(u => u.account_status === 'pending').length;

  return (
    <div data-testid="users-tab">
      {/* 待審批提示橫幅 */}
      {pendingCount > 0 && (
        <div className="mb-4 p-3 md:p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 md:h-6 md:w-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-medium text-orange-800">
                  有 {pendingCount} 個用戶等待審批
                </h3>
                <p className="text-xs md:text-sm text-orange-700 mt-1 hidden md:block">
                  請在「狀態」列點擊 ✓ 批准或 ✗ 拒絕用戶註冊申請
                </p>
              </div>
            </div>
            <Button
              onClick={() => setFilterStatus('pending')}
              className="bg-orange-600 hover:bg-orange-700 text-white text-sm w-full md:w-auto"
            >
              查看全部待審批
            </Button>
          </div>
        </div>
      )}

      {/* 頁面標題和操作區 */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">用戶管理</h1>
        
        {/* 手機版：過濾和新增按鈕分兩行顯示 */}
        <div className="flex flex-col gap-3">
          {/* 過濾器行 */}
          <div className="flex flex-wrap gap-2 items-center">
            {/* Global approval toggle - 只在桌面版顯示 */}
            {filterOrg !== 'all' && currentOrgSettings && (
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="text-sm font-medium text-gray-700">帳戶審批開關:</label>
                <button
                  onClick={handleToggleApprovalSetting}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    currentOrgSettings.require_approval ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      currentOrgSettings.require_approval ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-600">
                  {currentOrgSettings.require_approval ? '已開啟' : '已關閉'}
                </span>
              </div>
            )}
            
            {/* Pending approval badge - 只在桌面版顯示 */}
            {pendingCount > 0 && (
              <Button
                onClick={() => setFilterStatus('pending')}
                className={`hidden md:inline-flex ${filterStatus === 'pending' ? 'bg-red-600' : 'bg-orange-500'} hover:bg-orange-600`}
              >
                待審批 ({pendingCount})
              </Button>
            )}
            
            {/* Organization filter */}
            <select 
              value={filterOrg}
              onChange={(e) => setFilterOrg(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm flex-1 md:flex-none"
            >
              <option value="all">所有機構</option>
              {organizations.map(org => (
                <option key={org.organization_id} value={org.organization_id}>
                  {org.name}
                </option>
              ))}
            </select>
            
            {/* Status filter */}
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm flex-1 md:flex-none"
            >
              <option value="all">所有狀態</option>
              <option value="pending">待審批</option>
              <option value="approved">已批准</option>
              <option value="rejected">已拒絕</option>
            </select>
          </div>
          
          {/* 新增學生按鈕行 */}
          <Dialog open={addStudentDialogOpen} onOpenChange={setAddStudentDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" data-testid="add-student-btn">
                + 新增學生
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <span>新增學生</span>
                  <div className="flex items-center gap-4 text-sm font-normal">
                    {/* 批量模式切換 */}
                    <div className="flex items-center gap-2">
                      <span className={!batchMode ? 'text-gray-400' : 'text-green-600 font-medium'}>批量</span>
                      <button
                        type="button"
                        onClick={() => setBatchMode(!batchMode)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${
                          batchMode ? 'bg-green-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          batchMode ? 'left-5' : 'left-0.5'
                        }`} />
                      </button>
                    </div>
                    {/* 簡易/完整模式切換 - 只在非批量模式時顯示 */}
                    {!batchMode && (
                      <div className="flex items-center gap-2 border-l pl-4">
                        <span className={simpleMode ? 'text-blue-600' : 'text-gray-400'}>簡易版</span>
                        <button
                          type="button"
                          onClick={() => setSimpleMode(!simpleMode)}
                          className={`relative w-10 h-5 rounded-full transition-colors ${
                            simpleMode ? 'bg-blue-600' : 'bg-gray-300'
                          }`}
                        >
                          <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                            simpleMode ? 'left-0.5' : 'left-5'
                          }`} />
                        </button>
                        <span className={!simpleMode ? 'text-blue-600' : 'text-gray-400'}>完整版</span>
                      </div>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              
              {/* 批量模式 */}
              {batchMode ? (
                <form onSubmit={handleBatchCreateStudents} className="space-y-4">
                  <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700">
                    <strong>批量新增模式：</strong>每行輸入一個學生姓名，所有學生將使用相同的上課設定
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 機構選擇 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">機構</label>
                      <select
                        value={newStudent.organization_id}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, organization_id: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">選擇機構</option>
                        {organizations.map(org => (
                          <option key={org.organization_id} value={org.organization_id}>
                            {org.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* 教練選擇 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">負責教練</label>
                      <select
                        value={newStudent.coach_id}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, coach_id: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="">選擇教練</option>
                        {coaches
                          .filter(c => !newStudent.organization_id || c.organization_id === newStudent.organization_id)
                          .map(coach => (
                            <option key={coach.user_id} value={coach.user_id}>
                              {coach.coach_name || coach.username}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* 批量學生姓名輸入 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      學生姓名（每行一個）
                    </label>
                    <textarea
                      value={batchNames}
                      onChange={(e) => setBatchNames(e.target.value)}
                      placeholder="張三&#10;李四&#10;王五&#10;（每行輸入一個學生姓名）"
                      className="w-full px-3 py-2 border rounded-lg h-32 resize-none"
                      data-testid="batch-names-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      已輸入 {batchNames.split('\n').filter(n => n.trim()).length} 個學生
                    </p>
                  </div>
                  
                  {/* 上課日期 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">上課日期（共用）</label>
                    <div className="flex flex-wrap gap-2">
                      {['週一', '週二', '週三', '週四', '週五', '週六', '週日'].map((day, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => toggleClassDay(index)}
                          className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                            newStudent.class_days.includes(index)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* 共用設定 */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">上課時間（共用）</label>
                      <Input
                        value={newStudent.time_slot}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, time_slot: e.target.value }))}
                        placeholder="例如: 10:00-11:00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">上課地點（共用）</label>
                      <Input
                        value={newStudent.location}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="輸入上課地點"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">每堂費用（共用）</label>
                      <Input
                        type="number"
                        value={newStudent.fee_per_class}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, fee_per_class: e.target.value }))}
                        placeholder="輸入金額"
                        data-testid="batch-fee-input"
                      />
                    </div>
                  </div>
                  
                  {/* 費用預覽 */}
                  {newStudent.class_days.length > 0 && newStudent.fee_per_class && (
                    <div className="p-3 bg-gray-50 rounded-lg text-sm">
                      <span className="text-gray-600">每位學生月費預估：</span>
                      <span className="font-bold text-blue-600 ml-2">
                        ${(newStudent.class_days.length * (parseFloat(newStudent.fee_per_class) || 0) * 4).toLocaleString()}
                      </span>
                      <span className="text-gray-500 ml-2">
                        ({newStudent.class_days.length} 天 × ${newStudent.fee_per_class} × 4週)
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setAddStudentDialogOpen(false)}>
                      取消
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submittingStudent}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="batch-submit-btn"
                    >
                      {submittingStudent ? '創建中...' : `批量新增 ${batchNames.split('\n').filter(n => n.trim()).length} 個學生`}
                    </Button>
                  </div>
                </form>
              ) : (
                /* 原有的單個新增表單 */
                <form onSubmit={handleCreateStudent} className="space-y-4">
                {/* 簡易模式提示 */}
                {simpleMode && (
                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    簡易模式：所有欄位皆為選填，只需填寫學生姓名，系統將生成匹配編號供學生日後自行註冊綁定
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* 機構選擇 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">機構</label>
                    <select
                      value={newStudent.organization_id}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, organization_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">選擇機構</option>
                      {organizations.map(org => (
                        <option key={org.organization_id} value={org.organization_id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* 教練選擇 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">負責教練</label>
                    <select
                      value={newStudent.coach_id}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, coach_id: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="">選擇教練</option>
                      {coaches
                        .filter(c => !newStudent.organization_id || c.organization_id === newStudent.organization_id)
                        .map(coach => (
                          <option key={coach.user_id} value={coach.user_id}>
                            {coach.coach_name || coach.username}
                          </option>
                        ))}
                    </select>
                  </div>
                  
                  {/* 學生姓名 */}
                  {simpleMode ? (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        學生姓名
                      </label>
                      <Input
                        value={newStudent.student_name}
                        onChange={(e) => setNewStudent(prev => ({ ...prev, student_name: e.target.value }))}
                        placeholder="學生姓名（可用中文）"
                      />
                      <p className="text-xs text-gray-500 mt-1">學生日後可使用匹配編號自行註冊帳號</p>
                    </div>
                  ) : (
                    <>
                      {/* 完整模式：用戶名 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">登入帳號 *</label>
                        <Input
                          value={newStudent.username}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, username: e.target.value }))}
                          placeholder="登入用戶名"
                          required
                        />
                      </div>
                      
                      {/* 學生真實姓名 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">學生姓名</label>
                        <Input
                          value={newStudent.student_name}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, student_name: e.target.value }))}
                          placeholder="學生真實姓名"
                        />
                      </div>
                      
                      {/* 電郵 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">電郵 *</label>
                        <Input
                          type="email"
                          value={newStudent.email}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="student@example.com"
                          required
                        />
                      </div>
                      
                      {/* 電話 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">電話</label>
                        <Input
                          value={newStudent.phone}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="電話號碼"
                        />
                      </div>
                      
                      {/* 密碼 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">密碼 *</label>
                        <Input
                          type="text"
                          value={newStudent.password}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="登入密碼"
                          required
                        />
                      </div>
                      
                      {/* 學生編號 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">學生編號</label>
                        <Input
                          value={newStudent.student_id}
                          onChange={(e) => setNewStudent(prev => ({ ...prev, student_id: e.target.value }))}
                          placeholder="留空自動生成"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                {/* 上課日期選擇 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">上課日期</label>
                  <div className="flex gap-2 flex-wrap">
                    {['週一', '週二', '週三', '週四', '週五', '週六', '週日'].map((day, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => toggleClassDay(index)}
                        className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                          newStudent.class_days.includes(index)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    已選擇 {newStudent.class_days.length} 天
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* 每堂費用 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">每堂費用</label>
                    <Input
                      type="number"
                      value={newStudent.fee_per_class}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, fee_per_class: e.target.value }))}
                      placeholder="輸入金額"
                      min="0"
                    />
                  </div>
                  
                  {/* 上課時間 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">上課時間</label>
                    <Input
                      value={newStudent.time_slot}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, time_slot: e.target.value }))}
                      placeholder="10:00-11:00"
                    />
                  </div>
                  
                  {/* 上課地點 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">上課地點</label>
                    <Input
                      value={newStudent.location}
                      onChange={(e) => setNewStudent(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="教室位置"
                    />
                  </div>
                </div>
                
                {/* 費用預覽 */}
                {newStudent.class_days.length > 0 && newStudent.fee_per_class && parseFloat(newStudent.fee_per_class) > 0 && (
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      預估月費：{newStudent.class_days.length} 天 × ${newStudent.fee_per_class} × 4 週 = 
                      <span className="font-bold"> ${newStudent.class_days.length * parseFloat(newStudent.fee_per_class) * 4}</span>
                    </p>
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={submittingStudent}
                >
                  {submittingStudent ? '新增中...' : '新增學生'}
                </Button>
              </form>
              )}
            </DialogContent>
          </Dialog>
          
          {/* 學生創建成功對話框 */}
          <Dialog open={showStudentCreatedDialog} onOpenChange={(open) => {
            setShowStudentCreatedDialog(open);
            if (!open) {
              setCreatedStudentInfo(null);
              setBatchCreatedStudents([]);
            }
          }}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-center text-green-600">
                  {batchCreatedStudents.length > 0 ? '批量創建完成！' : '學生帳戶已創建成功！'}
                </DialogTitle>
              </DialogHeader>
              
              {/* 批量創建結果 */}
              {batchCreatedStudents.length > 0 ? (
                <div className="space-y-4">
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-green-700">
                      成功創建 <span className="font-bold">{batchCreatedStudents.filter(r => r.success).length}</span> / {batchCreatedStudents.length} 個學生
                    </p>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">姓名</th>
                          <th className="px-3 py-2 text-left">帳號</th>
                          <th className="px-3 py-2 text-left">密碼</th>
                          <th className="px-3 py-2 text-center">狀態</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {batchCreatedStudents.map((result, index) => (
                          <tr key={index} className={result.success ? '' : 'bg-red-50'}>
                            <td className="px-3 py-2">{result.name}</td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {result.success ? result.student.username : '-'}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {result.success ? result.student.password : '-'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {result.success ? (
                                <span className="text-green-600">✓</span>
                              ) : (
                                <span className="text-red-600" title={result.error}>✗</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <Button 
                    onClick={() => {
                      const successStudents = batchCreatedStudents.filter(r => r.success);
                      const text = successStudents.map(r => 
                        `${r.name}\t${r.student.username}\t${r.student.password}`
                      ).join('\n');
                      navigator.clipboard.writeText(`姓名\t帳號\t密碼\n${text}`);
                      toast.success('所有登入資訊已複製到剪貼板');
                    }}
                    className="w-full"
                    variant="outline"
                  >
                    複製所有登入資訊（可貼到 Excel）
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowStudentCreatedDialog(false);
                      setBatchCreatedStudents([]);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    確定
                  </Button>
                </div>
              ) : createdStudentInfo && (
                /* 單個學生創建結果 */
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <p className="text-center text-lg font-bold text-green-800">
                      {createdStudentInfo.student_name}
                    </p>
                    {createdStudentInfo.coach_name && (
                      <p className="text-center text-sm text-green-600">
                        負責教練：{createdStudentInfo.coach_name}
                      </p>
                    )}
                  </div>
                  
                  <div className="p-4 bg-gray-100 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">登入帳號：</span>
                      <span className="font-mono font-bold text-blue-600">{createdStudentInfo.username}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">登入密碼：</span>
                      <span className="font-mono font-bold text-blue-600">{createdStudentInfo.password}</span>
                    </div>
                    {createdStudentInfo.monthly_fee > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">月費：</span>
                        <span className="font-bold">${createdStudentInfo.monthly_fee}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-700">
                    <p className="font-medium mb-1">請保存此登入資訊！</p>
                    <p>學生可使用以上帳號密碼登入系統。</p>
                  </div>
                  
                  <Button 
                    onClick={() => {
                      const text = `學生：${createdStudentInfo.student_name}\n帳號：${createdStudentInfo.username}\n密碼：${createdStudentInfo.password}`;
                      navigator.clipboard.writeText(text);
                      toast.success('登入資訊已複製到剪貼板');
                    }}
                    className="w-full"
                    variant="outline"
                  >
                    複製登入資訊
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowStudentCreatedDialog(false);
                      setCreatedStudentInfo(null);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    確定
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* 手機版審批開關 */}
      {filterOrg !== 'all' && currentOrgSettings && (
        <div className="md:hidden mb-4 flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="text-sm font-medium text-gray-700">帳戶審批開關</label>
          <div className="flex items-center gap-2">
            <button
              onClick={handleToggleApprovalSetting}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                currentOrgSettings.require_approval ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  currentOrgSettings.require_approval ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-600">
              {currentOrgSettings.require_approval ? '開' : '關'}
            </span>
          </div>
        </div>
      )}

      {/* 桌面版表格 - 只在 md 及以上顯示 */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-max">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">用戶名稱</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">學生姓名</th>
              {currentUser.role === 'super_admin' && (
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">密碼</th>
              )}
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">電話</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">角色</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">月費</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">狀態</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">機構</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredUsers.map((user) => {
              const org = organizations.find(o => o.organization_id === user.organization_id);
              return (
                <tr key={user.user_id} data-testid="user-item" className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="font-medium text-sm">{user.username}</div>
                    {user.student_id && (
                      <div className="text-xs text-gray-500">{user.student_id}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {user.role === 'student' ? (
                      <span className="font-medium text-blue-700">{user.student_name || '-'}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  {currentUser.role === 'super_admin' && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{user.plain_password || 'N/A'}</code>
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{user.phone || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                      user.role === 'org_admin' ? 'bg-blue-100 text-blue-700' :
                      user.role === 'coach' ? 'bg-green-100 text-green-700' :
                      user.role === 'student' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-pink-100 text-pink-700'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {user.role === 'student' && user.monthly_fee ? (
                      <span className="font-medium text-green-700">${user.monthly_fee.toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        user.account_status === 'approved' ? 'bg-green-100 text-green-700' :
                        user.account_status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-orange-100 text-orange-700'
                      }`}>
                        {user.account_status === 'approved' ? '已批准' :
                         user.account_status === 'rejected' ? '已拒絕' :
                         '待審批'}
                      </span>
                      {user.account_status === 'pending' && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleApproveUser(user.user_id, 'approved')}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
                            title="批准此用戶"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleApproveUser(user.user_id, 'rejected')}
                            className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors"
                            title="拒絕此用戶"
                          >
                            ✗
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{org?.name || user.organization_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 flex-wrap">
                      {user.account_status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(user.user_id, 'approved')}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2"
                          >
                            批准
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleApproveUser(user.user_id, 'rejected')}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs px-2"
                          >
                            拒絕
                          </Button>
                        </>
                      )}
                      {user.role === 'student' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditStudent(user)}
                          className="text-xs px-2"
                          data-testid={`edit-student-${user.user_id}`}
                        >
                          編輯
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user);
                          setPasswordDialogOpen(true);
                        }}
                        className="text-xs px-2"
                      >
                        改密碼
                      </Button>
                      {user.role !== 'super_admin' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteUser(user)}
                          className="text-xs px-2"
                        >
                          刪除
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 手機版卡片列表 - 只在 md 以下顯示 */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => {
          const org = organizations.find(o => o.organization_id === user.organization_id);
          return (
            <div key={user.user_id} data-testid="user-item-mobile" className="bg-white rounded-lg shadow p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{user.username}</h3>
                  {user.role === 'student' && user.student_name && (
                    <p className="text-sm font-medium text-blue-700">{user.student_name}</p>
                  )}
                  {user.student_id && (
                    <p className="text-xs text-gray-500">{user.student_id}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                    user.role === 'org_admin' ? 'bg-blue-100 text-blue-700' :
                    user.role === 'coach' ? 'bg-green-100 text-green-700' :
                    user.role === 'student' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-pink-100 text-pink-700'
                  }`}>
                    {user.role}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.account_status === 'approved' ? 'bg-green-100 text-green-700' :
                    user.account_status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {user.account_status === 'approved' ? '已批准' :
                     user.account_status === 'rejected' ? '已拒絕' : '待審批'}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span className="text-gray-500">電話：</span>
                  <span>{user.phone || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-500">機構：</span>
                  <span>{org?.name || user.organization_id}</span>
                </div>
                {user.role === 'student' && user.monthly_fee > 0 && (
                  <div>
                    <span className="text-gray-500">月費：</span>
                    <span className="font-medium text-green-700">${user.monthly_fee.toLocaleString()}</span>
                  </div>
                )}
                {currentUser.role === 'super_admin' && user.plain_password && (
                  <div className="col-span-2">
                    <span className="text-gray-500">密碼：</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">{user.plain_password}</code>
                  </div>
                )}
              </div>
              
              {/* 操作按鈕 */}
              <div className="flex flex-wrap gap-2 pt-3 border-t">
                {user.account_status === 'pending' && (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleApproveUser(user.user_id, 'approved')}
                      className="bg-green-600 hover:bg-green-700 text-white flex-1"
                    >
                      批准
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApproveUser(user.user_id, 'rejected')}
                      className="bg-red-600 hover:bg-red-700 text-white flex-1"
                    >
                      拒絕
                    </Button>
                  </>
                )}
                {user.role === 'student' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditStudent(user)}
                    className="flex-1"
                  >
                    編輯
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedUser(user);
                    setPasswordDialogOpen(true);
                  }}
                  className="flex-1"
                >
                  改密碼
                </Button>
                {user.role !== 'super_admin' && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteUser(user)}
                    className="flex-1"
                  >
                    刪除
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="mt-4 text-sm text-gray-600">
        共 {filteredUsers.length} 位用戶
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>修改密碼 - {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">新密碼</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="輸入新密碼"
              />
            </div>
            <Button onClick={handleChangePassword} className="w-full">
              確認修改
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={userDetailsDialogOpen} onOpenChange={setUserDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>學生詳情 - {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {loadingDetails ? (
              <div className="text-center py-8 text-gray-500">載入中...</div>
            ) : userDetails ? (
              <>
                {/* Basic Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3">基本資料</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">姓名：</span>
                      <span className="font-medium">{userDetails.username}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">學生ID：</span>
                      <span className="font-medium">{userDetails.student_id || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">電郵：</span>
                      <span className="font-medium">{userDetails.email}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">電話：</span>
                      <span className="font-medium">{userDetails.phone}</span>
                    </div>
                  </div>
                </div>

                {/* Class Records */}
                <div>
                  <h3 className="font-semibold mb-3">課堂記錄</h3>
                  {userDetails.classRecords && userDetails.classRecords.length > 0 ? (
                    <div className="space-y-3">
                      {userDetails.classRecords.map((record, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-white">
                          <div className="mb-2">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-medium text-lg">{record.location_name || '未知地點'}</p>
                              <span className="text-sm text-gray-500">
                                {new Date(record.date).toLocaleDateString('zh-TW')}
                              </span>
                            </div>
                            {record.notes && (
                              <div className="bg-gray-50 p-3 rounded mt-2">
                                <p className="text-sm text-gray-600 mb-1">學生備註：</p>
                                <p className="text-sm text-gray-800">{record.notes}</p>
                              </div>
                            )}
                            {record.video_urls && record.video_urls.length > 0 && (
                              <div className="mt-3">
                                <p className="text-sm text-gray-600 mb-2">訓練視頻：</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {record.video_urls.map((videoUrl, vIndex) => (
                                    <video 
                                      key={vIndex}
                                      controls 
                                      className="w-full rounded border"
                                      src={`${window.location.origin}${videoUrl}`}
                                    >
                                      您的瀏覽器不支持視頻播放
                                    </video>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">暫無課堂記錄</p>
                  )}
                </div>

                {/* Task Completions */}
                <div>
                  <h3 className="font-semibold mb-3">任務完成記錄</h3>
                  {userDetails.taskCompletions && userDetails.taskCompletions.length > 0 ? (
                    <div className="space-y-2">
                      {userDetails.taskCompletions.map((task, index) => (
                        <div key={index} className="border rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium">{task.task_name}</p>
                              <p className="text-sm text-gray-600">
                                完成時間: {new Date(task.completed_at).toLocaleString('zh-TW')}
                              </p>
                            </div>
                            <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-700">
                              已完成
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">暫無任務完成記錄</p>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">無法載入用戶詳情</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 編輯學生對話框 */}
      <Dialog open={editStudentDialogOpen} onOpenChange={setEditStudentDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>編輯學生資料</DialogTitle>
          </DialogHeader>
          
          {editingStudentData && (
            <div className="space-y-4 py-4">
              {/* 學生姓名 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">學生姓名</label>
                <Input
                  value={editingStudentData.student_name}
                  onChange={(e) => setEditingStudentData({
                    ...editingStudentData,
                    student_name: e.target.value
                  })}
                  placeholder="輸入學生姓名"
                  data-testid="edit-student-name"
                />
              </div>
              
              {/* 負責教練 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">負責教練</label>
                <select
                  value={editingStudentData.coach_id}
                  onChange={(e) => setEditingStudentData({
                    ...editingStudentData,
                    coach_id: e.target.value
                  })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  data-testid="edit-coach-select"
                >
                  <option value="">-- 選擇教練 --</option>
                  {coaches
                    .filter(c => !editingStudentData.organization_id || c.organization_id === editingStudentData.organization_id)
                    .map(coach => (
                      <option key={coach.user_id} value={coach.user_id}>
                        {coach.coach_name || coach.username}
                      </option>
                    ))}
                </select>
              </div>
              
              {/* 上課日期 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">上課日期</label>
                <div className="flex flex-wrap gap-2">
                  {['週一', '週二', '週三', '週四', '週五', '週六', '週日'].map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => toggleEditClassDay(index)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        editingStudentData.class_days?.includes(index)
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`edit-day-${index}`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* 上課時間 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">上課時間</label>
                <Input
                  value={editingStudentData.time_slot}
                  onChange={(e) => setEditingStudentData({
                    ...editingStudentData,
                    time_slot: e.target.value
                  })}
                  placeholder="例如: 10:00-11:00"
                  data-testid="edit-time-slot"
                />
              </div>
              
              {/* 上課地點 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">上課地點</label>
                <Input
                  value={editingStudentData.location}
                  onChange={(e) => setEditingStudentData({
                    ...editingStudentData,
                    location: e.target.value
                  })}
                  placeholder="輸入上課地點"
                  data-testid="edit-location"
                />
              </div>
              
              {/* 費用設定 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">每堂費用</label>
                  <Input
                    type="number"
                    value={editingStudentData.fee_per_class}
                    onChange={(e) => {
                      const feePerClass = parseFloat(e.target.value) || 0;
                      const classDays = editingStudentData.class_days || [];
                      setEditingStudentData({
                        ...editingStudentData,
                        fee_per_class: feePerClass,
                        monthly_fee: classDays.length * feePerClass * 4
                      });
                    }}
                    placeholder="0"
                    data-testid="edit-fee-per-class"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">月費總計</label>
                  <Input
                    type="number"
                    value={editingStudentData.monthly_fee}
                    onChange={(e) => setEditingStudentData({
                      ...editingStudentData,
                      monthly_fee: parseFloat(e.target.value) || 0
                    })}
                    placeholder="0"
                    data-testid="edit-monthly-fee"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    自動計算: {editingStudentData.class_days?.length || 0} 天 × ${editingStudentData.fee_per_class || 0} × 4週
                  </p>
                </div>
              </div>
              
              {/* 操作按鈕 */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditStudentDialogOpen(false);
                    setEditingStudentData(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  onClick={handleSaveStudent}
                  disabled={savingStudent}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-student-btn"
                >
                  {savingStudent ? '儲存中...' : '儲存變更'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



// Page Configuration Tab
function PageConfigTab({ user }) {
  // Set initial tab based on user role
  const getInitialTab = () => {
    if (user.role === 'super_admin') {
      return 'org_entry';
    }
    return 'login'; // org_admin starts with login page
  };
  
  const [activePageTab, setActivePageTab] = useState(getInitialTab());
  const [configs, setConfigs] = useState({});
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);

  const pageTypes = [
    { key: 'org_entry', label: '機構輸入頁', orgType: 'system' },
    { key: 'login', label: '登入頁', orgType: 'specific' },
    { key: 'student_home', label: '學生主頁', orgType: 'specific' }
  ];

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    // Set default org ID based on user
    if (organizations.length > 0) {
      if (user.role === 'super_admin') {
        setSelectedOrgId(organizations[0].organization_id); // Default to first org
      } else {
        setSelectedOrgId(user.organization_id);
      }
    }
  }, [user, organizations]);

  useEffect(() => {
    if (selectedOrgId) {
      loadAllConfigs();
    }
  }, [selectedOrgId]);

  const loadOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/organizations`);
      setOrganizations(res.data);
    } catch (error) {
      console.error('Failed to load organizations');
    }
  };

  const loadAllConfigs = async () => {
    setLoading(true);
    try {
      const loadedConfigs = {};
      for (const pageType of pageTypes) {
        // org_entry uses 'system', others use selected org ID
        const orgId = pageType.orgType === 'system' ? 'system' : selectedOrgId;
        const res = await axios.get(`${API}/page-config/${pageType.key}?organization_id=${orgId}`);
        loadedConfigs[pageType.key] = res.data;
      }
      setConfigs(loadedConfigs);
    } catch (error) {
      toast.error('載入配置失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      updateConfigField(fieldName, res.data.url);
      toast.success('圖片已上傳');
    } catch (error) {
      toast.error('上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const updateConfigField = (field, value) => {
    setConfigs(prev => ({
      ...prev,
      [activePageTab]: {
        ...prev[activePageTab],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      const config = { ...configs[activePageTab] };
      
      // Ensure organization_id is set correctly
      const pageType = pageTypes.find(pt => pt.key === activePageTab);
      if (pageType.orgType === 'system') {
        config.organization_id = 'system';
      } else {
        config.organization_id = selectedOrgId;
      }
      
      console.log('Saving page config:', { page: activePageTab, config });
      
      await axios.put(`${API}/page-config/${activePageTab}`, config);
      toast.success('配置已儲存');
      
      // Reload configs to ensure we have the latest
      await loadAllConfigs();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('儲存失敗: ' + (error.response?.data?.detail || error.message));
    }
  };

  const currentConfig = configs[activePageTab] || {};
  const currentPageType = pageTypes.find(pt => pt.key === activePageTab);

  return (
    <div data-testid="page-config-tab">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">前端頁面配置</h1>

      {/* Organization Selector for super admin */}
      {user.role === 'super_admin' && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            選擇機構 (用於登入頁和學生主頁配置)
          </label>
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="w-full max-w-md px-4 py-2 border rounded-lg"
          >
            {organizations.map(org => (
              <option key={org.organization_id} value={org.organization_id}>
                {org.name} ({org.organization_id})
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-600 mt-1">
            注意：機構輸入頁使用系統級配置，不受此影響
          </p>
        </div>
      )}

      {/* Page Type Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        {pageTypes
          .filter(pageType => {
            // 機構輸入頁只有 super_admin 可見
            if (pageType.key === 'org_entry') {
              return user.role === 'super_admin';
            }
            return true;
          })
          .map(pageType => (
            <button
              key={pageType.key}
              onClick={() => setActivePageTab(pageType.key)}
              className={`px-4 py-2 font-medium transition-colors ${
                activePageTab === pageType.key
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {pageType.label}
            </button>
          ))}
      </div>

      {loading ? (
        <div>載入中...</div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          {/* Org Entry Page Config */}
          {activePageTab === 'org_entry' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">頁面標題</label>
                <Input
                  value={currentConfig.title || ''}
                  onChange={(e) => updateConfigField('title', e.target.value)}
                  placeholder="AI Investment"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">副標題</label>
                <Input
                  value={currentConfig.subtitle || ''}
                  onChange={(e) => updateConfigField('subtitle', e.target.value)}
                  placeholder="請輸入您的機構編號"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Logo圖片</label>
                <div className="flex items-center gap-4">
                  {currentConfig.logo_url && (
                    <img src={currentConfig.logo_url} alt="Logo" className="h-16 w-16 object-contain border rounded" />
                  )}
                  <label className="px-4 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo_url')}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? '上傳中...' : '上傳Logo'}
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">背景顏色/漸變</label>
                <Input
                  value={currentConfig.background_color || ''}
                  onChange={(e) => updateConfigField('background_color', e.target.value)}
                  placeholder="linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)"
                />
                <p className="text-xs text-gray-500 mt-1">可使用CSS顏色值或漸變</p>
              </div>
            </div>
          )}

          {/* Login Page Config */}
          {activePageTab === 'login' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">頁面標題</label>
                <Input
                  value={currentConfig.title || ''}
                  onChange={(e) => updateConfigField('title', e.target.value)}
                  placeholder="學生登入"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">副標題</label>
                <Input
                  value={currentConfig.subtitle || ''}
                  onChange={(e) => updateConfigField('subtitle', e.target.value)}
                  placeholder="歡迎回來"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Logo圖片</label>
                <div className="flex items-center gap-4">
                  {currentConfig.logo_url && (
                    <img src={currentConfig.logo_url} alt="Logo" className="h-16 w-16 object-contain border rounded" />
                  )}
                  <label className="px-4 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logo_url')}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? '上傳中...' : '上傳Logo'}
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">背景圖片</label>
                <div className="flex items-center gap-4">
                  {currentConfig.background_image && (
                    <img src={currentConfig.background_image} alt="Background" className="h-24 w-32 object-cover border rounded" />
                  )}
                  <label className="px-4 py-2 bg-gray-100 rounded cursor-pointer hover:bg-gray-200">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'background_image')}
                      className="hidden"
                      disabled={uploading}
                    />
                    {uploading ? '上傳中...' : '上傳背景圖'}
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">主題色</label>
                <div className="flex items-center gap-4">
                  <Input
                    type="color"
                    value={currentConfig.theme_color || '#FF6B35'}
                    onChange={(e) => updateConfigField('theme_color', e.target.value)}
                    className="w-20 h-10"
                  />
                  <Input
                    value={currentConfig.theme_color || ''}
                    onChange={(e) => updateConfigField('theme_color', e.target.value)}
                    placeholder="#FF6B35"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Student Home Config */}
          {activePageTab === 'student_home' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">歡迎文字</label>
                <Input
                  value={currentConfig.welcome_text || ''}
                  onChange={(e) => updateConfigField('welcome_text', e.target.value)}
                  placeholder="歡迎回來"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">副標題</label>
                <Input
                  value={currentConfig.subtitle || ''}
                  onChange={(e) => updateConfigField('subtitle', e.target.value)}
                  placeholder="準備好開始今天的訓練了嗎？"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentConfig.show_stats !== false}
                  onChange={(e) => updateConfigField('show_stats', e.target.checked)}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">顯示統計卡片</label>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="mt-8 pt-6 border-t">
            <div className="mb-3 text-sm text-gray-600">
              <strong>當前配置：</strong> {currentPageType?.orgType === 'system' ? '系統級 (所有機構共用)' : `機構: ${selectedOrgId}`}
            </div>
            <Button 
              onClick={handleSave}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              儲存配置到 {currentPageType?.orgType === 'system' ? '系統' : selectedOrgId}
            </Button>
          </div>

          {/* Preview Note */}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800 mb-2">
              💡 提示：儲存後，請重新整理前端頁面以查看變更
            </p>
            <p className="text-xs text-gray-600">
              {currentPageType?.orgType === 'system' 
                ? '機構輸入頁為系統級配置，影響所有機構的首頁' 
                : '登入頁和學生主頁為機構專屬配置'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function AnnouncementsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', images: [] });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/announcements`);
      setAnnouncements(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請先登入');
        setUploading(false);
        return;
      }

      const uploadedUrls = [];
      for (const file of files) {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        const res = await axios.post(`${API}/media/upload`, formDataObj, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        uploadedUrls.push(res.data.media_url);
      }
      setFormData({...formData, images: [...formData.images, ...uploadedUrls]});
      toast.success(`已上傳 ${files.length} 張圖片`);
    } catch (error) {
      console.error('圖片上傳錯誤:', error.response?.data || error);
      if (error.response?.status === 403) {
        toast.error('沒有權限上傳。請確認您是管理員身份。');
      } else if (error.response?.status === 401) {
        toast.error('登入已過期，請重新登入');
      } else {
        toast.error('上傳失敗：' + (error.response?.data?.detail || '未知錯誤'));
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({...formData, images: newImages});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast.error('請填寫標題和內容');
      return;
    }
    
    try {
      await axios.post(`${API}/announcements`, formData);
      toast.success('公告已發布');
      setDialogOpen(false);
      setFormData({ title: '', content: '', images: [] });
      loadAnnouncements();
    } catch (error) {
      console.error('公告發布錯誤:', error.response?.data || error);
      toast.error('發布失敗：' + (error.response?.data?.detail || '未知錯誤'));
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('確定要刪除這個公告嗎？')) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcementId}`);
      toast.success('公告已刪除');
      loadAnnouncements();
    } catch (error) {
      console.error('刪除公告錯誤:', error.response?.data || error);
      toast.error('刪除失敗：' + (error.response?.data?.detail || '未知錯誤'));
    }
  };

  return (
    <div data-testid="announcements-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">公告管理</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-announcement-btn">發布公告</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>發布新公告</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">標題</label>
                <Input
                  data-testid="announcement-title-input"
                  placeholder="輸入公告標題"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">內容</label>
                <Textarea
                  data-testid="announcement-content-input"
                  placeholder="輸入公告內容"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={6}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">圖片</label>
                <label className="cursor-pointer inline-block px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? '上傳中...' : '選擇圖片'}
                </label>
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={img.startsWith('http') ? img : `${window.location.origin}/api/media-files${img.replace('/media', '')}`} 
                          alt="" 
                          className="w-full h-24 object-cover rounded" 
                          onError={(e) => {
                            console.error('圖片載入失敗:', img);
                            e.target.src = 'https://via.placeholder.com/150?text=圖片載入失敗';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button data-testid="submit-announcement-btn" type="submit" className="w-full">發布</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {announcements.map((announcement) => (
          <div key={announcement.announcement_id} className="bg-white rounded-lg shadow p-6" data-testid="announcement-item">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-semibold text-lg">{announcement.title}</h3>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(announcement.announcement_id)}
              >
                刪除
              </Button>
            </div>
            <p className="text-gray-600 mb-4 whitespace-pre-wrap">{announcement.content}</p>
            {announcement.images && announcement.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {announcement.images.map((img, idx) => (
                  <img 
                    key={idx} 
                    src={img.startsWith('http') ? img : `${window.location.origin}/api/media-files${img.replace('/media', '')}`} 
                    alt="" 
                    className="w-full h-32 object-cover rounded" 
                    onError={(e) => {
                      console.error('圖片載入失敗:', img);
                      e.target.src = 'https://via.placeholder.com/150?text=圖片載入失敗';
                    }}
                  />
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-4">
              發布時間：{new Date(announcement.created_at).toLocaleString('zh-TW')}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: ''
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/locations?name=${encodeURIComponent(formData.name)}&address=${encodeURIComponent(formData.address || '')}`);
      toast.success('地點已新增');
      setDialogOpen(false);
      setFormData({ name: '', address: '' });
      loadLocations();
    } catch (error) {
      toast.error('新增失敗');
    }
  };

  return (
    <div data-testid="locations-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">地點管理</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-location-btn">新增地點</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>新增地點</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">地點名稱</label>
                <Input
                  data-testid="location-name-input"
                  placeholder="例如：中環分店"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">詳細地址（可選）</label>
                <Textarea
                  data-testid="location-address-input"
                  placeholder="例如：香港中環皇后大道中100號"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                />
              </div>
              <Button data-testid="submit-location-btn" type="submit" className="w-full">新增</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {locations.map((location) => (
          <div key={location.location_id} className="bg-white rounded-lg shadow p-4" data-testid="location-item">
            <h3 className="font-semibold text-lg mb-2">{location.name}</h3>
            {location.address && (
              <p className="text-sm text-gray-600">
                <span className="inline-block mr-1">📍</span>
                {location.address}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [presets, setPresets] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    task_type: 'physical',
    date: new Date().toISOString().split('T')[0],
    scheduled_time: '',
    points: 1,
    status: 'published'
  });
  const [editingTask, setEditingTask] = useState(null);
  const [useCustomName, setUseCustomName] = useState(false);

  useEffect(() => {
    loadTasks();
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/preset-tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPresets(res.data);
    } catch (error) {
      console.error('載入預設任務錯誤:', error);
    }
  };

  const handleTaskNameChange = (presetId) => {
    const selectedPreset = presets.find(p => p.preset_id === presetId);
    if (selectedPreset) {
      setFormData({ 
        ...formData, 
        name: selectedPreset.name,
        description: selectedPreset.description,
        points: selectedPreset.points
      });
    }
  };

  const getPresetsByType = (type) => {
    return presets.filter(p => p.task_type === type);
  };

  const loadTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請重新登入');
        return;
      }
      
      const res = await axios.get(`${API}/daily-tasks/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setTasks(res.data);
    } catch (error) {
      console.error('載入任務錯誤:', error);
      if (error.response?.status === 401) {
        toast.error('認證失敗，請重新登入');
      } else if (error.response?.status === 403) {
        toast.error('沒有權限訪問任務管理');
      } else {
        toast.error('載入任務失敗: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        // Update existing task
        await axios.put(`${API}/daily-tasks/${editingTask.task_id}`, formData);
        toast.success('任務已更新');
      } else {
        // Create new task
        await axios.post(`${API}/daily-tasks`, formData);
        toast.success('任務已創建');
      }
      
      setDialogOpen(false);
      setEditingTask(null);
      resetForm();
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || '操作失敗');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      description: task.description,
      task_type: task.task_type,
      date: task.date,
      scheduled_time: task.scheduled_time || '',
      points: task.points,
      status: task.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('確定要刪除此任務嗎？')) return;
    
    try {
      await axios.delete(`${API}/daily-tasks/${taskId}`);
      toast.success('任務已刪除');
      loadTasks();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      task_type: 'physical',
      date: new Date().toISOString().split('T')[0],
      scheduled_time: '',
      points: 1,
      status: 'published'
    });
  };

  const handleGenerate = async () => {
    try {
      await axios.post(`${API}/daily-tasks/generate?date=${formData.date}`);
      toast.success('隨機任務已生成');
      loadTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || '生成失敗');
    }
  };

  return (
    <div data-testid="tasks-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">任務管理</h1>
        <div className="flex gap-3">
          <Button onClick={handleGenerate} variant="outline">
            生成隨機任務
          </Button>
          <Button 
            onClick={() => {
              resetForm();
              setEditingTask(null);
              setDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            新增任務
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">任務名稱</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">描述</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">類型</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">日期</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">預定時間</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">積分</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">狀態</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">操作</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                  暫無任務
                </td>
              </tr>
            ) : (
              tasks.map((task) => (
                <tr key={task.task_id} className="border-t hover:bg-gray-50">
                  <td className="px-6 py-4 font-medium">{task.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{task.description}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      task.task_type === 'physical' ? 'bg-blue-100 text-blue-700' :
                      task.task_type === 'stretching' ? 'bg-green-100 text-green-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {task.task_type === 'physical' ? '體能' :
                       task.task_type === 'stretching' ? '拉伸' : '跳躍'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">{task.date}</td>
                  <td className="px-6 py-4 text-sm">
                    {task.scheduled_time ? new Date(task.scheduled_time).toLocaleString('zh-TW') : '-'}
                  </td>
                  <td className="px-6 py-4">{task.points}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      task.status === 'published' ? 'bg-green-100 text-green-700' :
                      task.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.status === 'published' ? '已發佈' :
                       task.status === 'scheduled' ? '已排程' : '草稿'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(task)}>
                        編輯
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleDelete(task.task_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        刪除
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTask ? '編輯任務' : '新增任務'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">任務類型</label>
                <Select value={formData.task_type} onValueChange={(value) => {
                  setFormData({...formData, task_type: value, name: '', description: ''});
                  setUseCustomName(false);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">體能訓練</SelectItem>
                    <SelectItem value="stretching">拉伸運動</SelectItem>
                    <SelectItem value="jumping">跳躍訓練</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">任務名稱</label>
                {!useCustomName && getPresetsByType(formData.task_type).length > 0 ? (
                  <div className="space-y-2">
                    <Select value={formData.name} onValueChange={handleTaskNameChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="選擇預設任務" />
                      </SelectTrigger>
                      <SelectContent>
                        {getPresetsByType(formData.task_type).map((preset) => (
                          <SelectItem key={preset.preset_id} value={preset.preset_id}>
                            {preset.name} ({preset.points}積分)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setUseCustomName(true);
                        setFormData({...formData, name: '', description: ''});
                      }}
                      className="text-blue-600 hover:text-blue-700 h-auto p-0"
                    >
                      使用自訂名稱
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="例如：俯臥撐"
                      required
                    />
                    {getPresetsByType(formData.task_type).length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUseCustomName(false);
                          setFormData({...formData, name: '', description: ''});
                        }}
                        className="text-blue-600 hover:text-blue-700 h-auto p-0"
                      >
                        選擇預設名稱
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">任務描述</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="描述任務的具體要求"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">任務日期</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">預定發佈時間（可選）</label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">積分</label>
                <Input
                  type="number"
                  min="1"
                  value={formData.points}
                  onChange={(e) => setFormData({...formData, points: parseInt(e.target.value)})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">狀態</label>
                <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">草稿</SelectItem>
                    <SelectItem value="scheduled">已排程</SelectItem>
                    <SelectItem value="published">已發佈</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingTask ? '更新' : '創建'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideosTab() {
  const [videos, setVideos] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '',
    intro_text: '',
    video_url: '' 
  });
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [albumImages, setAlbumImages] = useState([]);
  const videoFileInputRef = useRef(null);
  const imageFileInputRef = useRef(null);

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast.error('影片文件過大，請選擇小於100MB的文件');
      return;
    }
    
    setUploadingVideo(true);
    toast.info('正在上傳影片...');
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請先登入');
        setUploadingVideo(false);
        return;
      }

      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const res = await axios.post(`${API}/media/upload`, uploadFormData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setFormData({...formData, video_url: res.data.media_url});
      toast.success('影片上傳成功');
    } catch (error) {
      console.error('影片上傳錯誤:', error.response?.data || error);
      if (error.response?.status === 403) {
        toast.error('沒有權限上傳。請確認您是管理員身份。');
      } else if (error.response?.status === 401) {
        toast.error('登入已過期，請重新登入');
      } else {
        toast.error('影片上傳失敗：' + (error.response?.data?.detail || '未知錯誤'));
      }
    } finally {
      setUploadingVideo(false);
      if (videoFileInputRef.current) {
        videoFileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    toast.info(`正在上傳 ${files.length} 張圖片...`);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請先登入');
        setUploadingImages(false);
        return;
      }

      const uploadPromises = files.map(async (file) => {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        const res = await axios.post(`${API}/media/upload`, uploadFormData, {
          headers: { 
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        return res.data.media_url;
      });
      
      const uploadedUrls = await Promise.all(uploadPromises);
      setAlbumImages([...albumImages, ...uploadedUrls]);
      toast.success(`${uploadedUrls.length} 張圖片上傳成功`);
    } catch (error) {
      console.error('圖片上傳錯誤:', error.response?.data || error);
      if (error.response?.status === 403) {
        toast.error('沒有權限上傳。請確認您是管理員身份。');
      } else if (error.response?.status === 401) {
        toast.error('登入已過期，請重新登入');
      } else {
        toast.error('圖片上傳失敗：' + (error.response?.data?.detail || '未知錯誤'));
      }
    } finally {
      setUploadingImages(false);
      if (imageFileInputRef.current) {
        imageFileInputRef.current.value = '';
      }
    }
  };

  const removeImage = (index) => {
    setAlbumImages(albumImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.video_url) {
      toast.error('請填寫標題並上傳影片');
      return;
    }
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('video_url', formData.video_url);
      submitData.append('description', formData.description || '');
      submitData.append('intro_text', formData.intro_text || '');
      submitData.append('album_images', JSON.stringify(albumImages));
      
      await axios.post(`${API}/videos`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('影片已新增');
      setDialogOpen(false);
      setFormData({ title: '', description: '', intro_text: '', video_url: '' });
      setAlbumImages([]);
      loadVideos();
    } catch (error) {
      console.error('影片新增錯誤:', error.response?.data || error);
      toast.error('新增失敗：' + (error.response?.data?.detail || '未知錯誤'));
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('確定要刪除這個影片嗎？')) return;
    
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      toast.success('影片已刪除');
      loadVideos();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  return (
    <div data-testid="videos-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">影片管理</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-video-btn">新增影片</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>新增影片</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">影片標題</label>
                <Input
                  data-testid="video-title-input"
                  placeholder="輸入影片標題"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">影片介紹</label>
                <Textarea
                  placeholder="輸入影片介紹文字..."
                  value={formData.intro_text}
                  onChange={(e) => setFormData({...formData, intro_text: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">影片描述</label>
                <Textarea
                  placeholder="輸入影片詳細描述..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">上傳影片</label>
                <input
                  ref={videoFileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => videoFileInputRef.current?.click()}
                    disabled={uploadingVideo}
                    className="flex-1"
                    variant="outline"
                  >
                    {uploadingVideo ? '上傳中...' : '📤 選擇影片文件'}
                  </Button>
                  <Input
                    placeholder="或輸入影片URL"
                    value={formData.video_url}
                    onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                    className="flex-1"
                  />
                </div>
                {formData.video_url && (
                  <p className="text-sm text-green-600 mt-2">✓ 影片已設定</p>
                )}
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">相冊圖片</label>
                <input
                  ref={imageFileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => imageFileInputRef.current?.click()}
                  disabled={uploadingImages}
                  className="w-full"
                  variant="outline"
                >
                  {uploadingImages ? '上傳中...' : '📸 上傳相冊圖片（可多選）'}
                </Button>
                
                {albumImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {albumImages.map((url, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={url.startsWith('http') ? url : `${window.location.origin}/api/media-files${url.replace('/media', '')}`}
                          alt={`相冊 ${index + 1}`}
                          className="w-full h-24 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <Button 
                data-testid="submit-video-btn" 
                type="submit" 
                className="w-full"
                disabled={uploadingVideo || uploadingImages}
              >
                新增影片
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {videos.map((video) => (
          <div key={video.video_id} className="bg-white rounded-lg shadow p-4" data-testid="video-item">
            <div className="mb-3">
              {video.video_url && (
                <video 
                  controls 
                  className="w-full rounded border mb-2"
                  src={video.video_url.startsWith('http') ? video.video_url : `${window.location.origin}/api/media-files${video.video_url.replace('/media', '')}`}
                >
                  您的瀏覽器不支持視頻播放
                </video>
              )}
            </div>
            <p className="font-semibold mb-2">{video.title}</p>
            {video.intro_text && (
              <p className="text-sm text-gray-600 mb-2">{video.intro_text}</p>
            )}
            {video.album_images && video.album_images.length > 0 && (
              <div className="flex gap-1 mb-2 overflow-x-auto">
                {video.album_images.slice(0, 3).map((img, idx) => (
                  <img 
                    key={idx}
                    src={img.startsWith('http') ? img : `${window.location.origin}/api/media-files${img.replace('/media', '')}`}
                    alt={`相冊 ${idx + 1}`}
                    className="w-16 h-16 object-cover rounded"
                  />
                ))}
                {video.album_images.length > 3 && (
                  <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-600">
                    +{video.album_images.length - 3}
                  </div>
                )}
              </div>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={() => handleDelete(video.video_id)}
              className="w-full mt-2"
            >
              刪除
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [replyDialog, setReplyDialog] = useState(null);
  const [reply, setReply] = useState('');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions`);
      setQuestions(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleReply = async (questionId) => {
    try {
      await axios.put(`${API}/questions/${questionId}/reply?reply=${encodeURIComponent(reply)}`);
      toast.success('已回覆');
      setReplyDialog(null);
      setReply('');
      loadQuestions();
    } catch (error) {
      toast.error('回覆失敗');
    }
  };

  return (
    <div data-testid="questions-tab">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">提問管理</h1>
      <div className="space-y-4">
        {questions.map((question) => (
          <div key={question.question_id} className="bg-white rounded-lg shadow p-6" data-testid="question-item">
            <p className="text-gray-700 mb-4">{question.content}</p>
            {question.status === 'pending' ? (
              <Dialog open={replyDialog === question.question_id} onOpenChange={(open) => setReplyDialog(open ? question.question_id : null)}>
                <DialogTrigger asChild>
                  <Button data-testid="reply-question-btn" size="sm">回覆</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>回覆問題</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">問題內容</label>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded">{question.content}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">回覆內容</label>
                      <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="輸入您的回覆..."
                        rows={5}
                      />
                    </div>
                    <Button onClick={() => handleReply(question.question_id)} className="w-full">
                      發送回覆
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <p className="text-green-600 text-sm">已回覆</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AwardConfigTab({ user }) {
  const [config, setConfig] = useState({ event_names: [], competition_items: [] });
  const [newEventName, setNewEventName] = useState('');
  const [newCompetitionItem, setNewCompetitionItem] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/award-config?organization_id=${user.organization_id}`);
      setConfig(res.data);
    } catch (error) {
      console.error('載入配置錯誤:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEventName = () => {
    if (!newEventName.trim()) {
      toast.error('請輸入賽事名稱');
      return;
    }
    if (config.event_names.includes(newEventName.trim())) {
      toast.error('該賽事名稱已存在');
      return;
    }
    setConfig({
      ...config,
      event_names: [...config.event_names, newEventName.trim()]
    });
    setNewEventName('');
  };

  const handleRemoveEventName = (index) => {
    setConfig({
      ...config,
      event_names: config.event_names.filter((_, i) => i !== index)
    });
  };

  const handleAddCompetitionItem = () => {
    if (!newCompetitionItem.trim()) {
      toast.error('請輸入比賽項目');
      return;
    }
    if (config.competition_items.includes(newCompetitionItem.trim())) {
      toast.error('該比賽項目已存在');
      return;
    }
    setConfig({
      ...config,
      competition_items: [...config.competition_items, newCompetitionItem.trim()]
    });
    setNewCompetitionItem('');
  };

  const handleRemoveCompetitionItem = (index) => {
    setConfig({
      ...config,
      competition_items: config.competition_items.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/award-config`, {
        organization_id: user.organization_id,
        event_names: config.event_names,
        competition_items: config.competition_items
      });
      toast.success('配置已保存');
      loadConfig();
    } catch (error) {
      console.error('保存配置錯誤:', error);
      toast.error('保存失敗');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">獎項配置</h1>
        <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
          保存配置
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">載入中...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 預設賽事名稱 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">預設賽事名稱</h2>
            <p className="text-sm text-gray-600 mb-4">學生添加獎項時可選擇以下預設選項</p>
            
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="輸入賽事名稱"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddEventName()}
                className="flex-1"
              />
              <Button onClick={handleAddEventName}>添加</Button>
            </div>

            <div className="space-y-2">
              {config.event_names.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">暫無預設賽事名稱</p>
              ) : (
                config.event_names.map((name, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded p-3">
                    <span className="text-gray-800">{name}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveEventName(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      刪除
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* 預設比賽項目 */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">預設比賽項目</h2>
            <p className="text-sm text-gray-600 mb-4">學生添加獎項時可選擇以下預設選項</p>
            
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="輸入比賽項目"
                value={newCompetitionItem}
                onChange={(e) => setNewCompetitionItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCompetitionItem()}
                className="flex-1"
              />
              <Button onClick={handleAddCompetitionItem}>添加</Button>
            </div>

            <div className="space-y-2">
              {config.competition_items.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">暫無預設比賽項目</p>
              ) : (
                config.competition_items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 rounded p-3">
                    <span className="text-gray-800">{item}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveCompetitionItem(index)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      刪除
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Pages Management Tab
function PagesTab() {
  const [pages, setPages] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPage, setEditPage] = useState(null);
  const [formData, setFormData] = useState({ 
    page_name: '', 
    title: '', 
    content: '',
    images: [],
    videos: []
  });

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    try {
      const res = await axios.get(`${API}/pages`);
      setPages(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editPage) {
        await axios.put(`${API}/pages/${editPage.page_id}`, formData);
        toast.success('頁面已更新');
      } else {
        await axios.post(`${API}/pages?page_name=${encodeURIComponent(formData.page_name)}&title=${encodeURIComponent(formData.title)}&content=${encodeURIComponent(formData.content)}`);
        toast.success('頁面已新增');
      }
      setDialogOpen(false);
      setEditPage(null);
      setFormData({ page_name: '', title: '', content: '', images: [], videos: [] });
      loadPages();
    } catch (error) {
      toast.error('操作失敗');
    }
  };

  const handleEdit = (page) => {
    setEditPage(page);
    setFormData({
      page_name: page.page_name,
      title: page.title,
      content: page.content,
      images: page.images || [],
      videos: page.videos || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('確定要刪除此頁面嗎？')) return;
    try {
      await axios.delete(`${API}/pages/${pageId}`);
      toast.success('頁面已刪除');
      loadPages();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  return (
    <div data-testid="pages-tab">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">前端頁面配置</h1>

      {/* 頁面管理 */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">頁面管理</h2>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditPage(null);
              setFormData({ page_name: '', title: '', content: '', images: [], videos: [] });
            }
          }}>
          <DialogTrigger asChild>
            <Button data-testid="add-page-btn">新增頁面</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editPage ? '編輯頁面' : '新增頁面'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">頁面名稱</label>
                <Input
                  data-testid="page-name-input"
                  placeholder="例如: home, about, custom-page"
                  value={formData.page_name}
                  onChange={(e) => setFormData({...formData, page_name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">頁面標題</label>
                <Input
                  data-testid="page-title-input"
                  placeholder="頁面標題"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">頁面內容</label>
                <Textarea
                  data-testid="page-content-input"
                  placeholder="輸入頁面內容（支持HTML）"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={10}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">圖片URL（每行一個）</label>
                  <Textarea
                    placeholder="https://example.com/image1.jpg"
                    value={formData.images.join('\n')}
                    onChange={(e) => setFormData({...formData, images: e.target.value.split('\n').filter(Boolean)})}
                    rows={4}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">影片URL（每行一個）</label>
                  <Textarea
                    placeholder="https://youtube.com/watch?v=..."
                    value={formData.videos.join('\n')}
                    onChange={(e) => setFormData({...formData, videos: e.target.value.split('\n').filter(Boolean)})}
                    rows={4}
                  />
                </div>
              </div>
              <Button data-testid="submit-page-btn" type="submit" className="w-full">
                {editPage ? '更新頁面' : '新增頁面'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {pages.map((page) => (
          <div key={page.page_id} className="bg-white rounded-lg shadow p-6" data-testid="page-item">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-lg mb-1">{page.title}</h3>
                <p className="text-sm text-gray-600">{page.page_name}</p>
              </div>
              <div className={`px-2 py-1 rounded text-xs ${page.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                {page.published ? '已發布' : '草稿'}
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-4 line-clamp-3">
              {page.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleEdit(page)}>編輯</Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(page.page_id)}>刪除</Button>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}

// Media Library Tab
function MediaTab() {
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, images, videos

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const res = await axios.get(`${API}/media`);
      setMedia(res.data);
    } catch (error) {
      toast.error('載入失敗');
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(`已上傳 ${files.length} 個文件`);
      loadMedia();
    } catch (error) {
      toast.error('上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('確定要刪除此媒體文件嗎？')) return;
    try {
      await axios.delete(`${API}/media/${mediaId}`);
      toast.success('已刪除');
      loadMedia();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL已複製到剪貼板');
  };

  const filteredMedia = media.filter(item => {
    if (filter === 'images') return item.mime_type?.startsWith('image/');
    if (filter === 'videos') return item.mime_type?.startsWith('video/');
    return true;
  });

  return (
    <div data-testid="media-tab">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">媒體庫</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleUpload}
              className="hidden"
              disabled={uploading}
              data-testid="upload-media-input"
            />
            {uploading ? '上傳中...' : '上傳文件'}
          </label>
          <div className="flex gap-2">
            <Button 
              variant={filter === 'all' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('all')}
            >
              全部
            </Button>
            <Button 
              variant={filter === 'images' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('images')}
            >
              圖片
            </Button>
            <Button 
              variant={filter === 'videos' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setFilter('videos')}
            >
              影片
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredMedia.map((item) => (
          <div key={item.media_id} className="bg-white rounded-lg shadow overflow-hidden" data-testid="media-item">
            <div className="aspect-video bg-gray-100 flex items-center justify-center overflow-hidden">
              {item.mime_type?.startsWith('image/') ? (
                <img src={item.url} alt={item.filename} className="w-full h-full object-cover" />
              ) : item.mime_type?.startsWith('video/') ? (
                <video src={item.url} className="w-full h-full object-cover" />
              ) : (
                <div className="text-gray-400">
                  <FileText size={48} />
                </div>
              )}
            </div>
            <div className="p-3">
              <p className="text-sm font-medium truncate mb-2">{item.filename}</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => copyToClipboard(item.url)}
                >
                  複製URL
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => handleDelete(item.media_id)}
                >
                  刪除
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredMedia.length === 0 && (
        <div className="text-center text-gray-500 py-12">
          <p>暫無媒體文件</p>
        </div>
      )}
    </div>
  );
}



// Super Admin Settings Tab
function SuperAdminSettingsTab({ user }) {
  const [newUsername, setNewUsername] = useState(user.username);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [orgSettings, setOrgSettings] = useState({
    disable_org_entry: false
  });
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
    } catch (error) {
      console.error('載入機構列表失敗:', error);
      toast.error('載入機構列表失敗');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const loadOrgSettings = async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgSettings({
        disable_org_entry: response.data.disable_org_entry || false
      });
    } catch (error) {
      console.error('載入機構設置失敗:', error);
      toast.error('載入機構設置失敗');
    }
  };

  const updateOrgSettings = async () => {
    if (!selectedOrg) {
      toast.error('請先選擇機構');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/organizations/${selectedOrg}`,
        orgSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('機構設置已更新');
    } catch (error) {
      console.error('更新機構設置失敗:', error);
      toast.error('更新失敗');
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrg(orgId);
    if (orgId) {
      loadOrgSettings(orgId);
    }
  };

  const handleUpdateUsername = async () => {
    if (!newUsername || newUsername === user.username) {
      toast.error('請輸入新的用戶名稱');
      return;
    }

    try {
      await axios.put(`${API}/users/${user.user_id}`, { username: newUsername });
      toast.success('用戶名稱已更新，請重新登入');
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/admin/login';
      }, 2000);
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error('請填寫所有欄位');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('密碼不一致');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('密碼長度至少6個字元');
      return;
    }

    try {
      await axios.post(`${API}/users/${user.user_id}/change-password`, {
        new_password: newPassword
      });
      toast.success('密碼已更新');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('更新失敗');
    }
  };

  return (
    <div data-testid="super-admin-settings-tab">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">超級管理員設定</h1>

      <div className="space-y-6">
        {/* 機構全局設置 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">機構全局設置</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">選擇機構</label>
              <Select value={selectedOrg} onValueChange={handleOrgChange}>
                <SelectTrigger>
                  <SelectValue placeholder="請選擇要設置的機構" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map((org) => (
                    <SelectItem key={org.organization_id} value={org.organization_id}>
                      {org.name} ({org.organization_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedOrg && (
              <>
                <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                  <div>
                    <h3 className="font-medium text-gray-900">關閉機構輸入頁面</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      啟用後，訪問首頁將直接跳轉到該機構的登入頁面，不顯示機構編號輸入
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={orgSettings.disable_org_entry}
                      onChange={(e) => setOrgSettings({ ...orgSettings, disable_org_entry: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                
                <Button onClick={updateOrgSettings} className="w-full bg-blue-600 hover:bg-blue-700">
                  保存機構設置
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Change Username */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">修改登入用戶名</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">當前用戶名</label>
              <Input value={user.username} disabled className="bg-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">新用戶名</label>
              <Input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="輸入新的登入用戶名"
              />
            </div>
            <Button onClick={handleUpdateUsername} className="w-full">
              更新用戶名
            </Button>
            <p className="text-sm text-amber-600">
              ⚠️ 更新後需要重新登入
            </p>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">修改密碼</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">新密碼</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="輸入新密碼（至少6個字元）"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">確認密碼</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="再次輸入新密碼"
              />
            </div>
            <Button onClick={handleUpdatePassword} className="w-full bg-blue-600 hover:bg-blue-700">
              更新密碼
            </Button>
          </div>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">系統資訊</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">角色：</span>
              <span className="font-semibold">超級管理員</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">用戶ID：</span>
              <span className="font-mono text-xs">{user.user_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">電郵：</span>
              <span>{user.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Inbox Tab - for receiving student questions
function InboxTab({ user }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [reply, setReply] = useState('');

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions`);
      setQuestions(res.data);
    } catch (error) {
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async () => {
    if (!reply.trim()) {
      toast.error('請輸入回覆內容');
      return;
    }

    try {
      await axios.put(`${API}/questions/${selectedQuestion.question_id}/reply`, {
        reply: reply
      });
      toast.success('回覆已發送');
      setReplyDialogOpen(false);
      setReply('');
      setSelectedQuestion(null);
      loadQuestions();
    } catch (error) {
      toast.error('回覆失敗');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">收件夾</h1>
        <Button onClick={loadQuestions} variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          重新整理
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center text-gray-500">載入中...</div>
        ) : questions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">暫無提問</div>
        ) : (
          <div className="divide-y">
            {questions.map((question) => (
              <div key={question.question_id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      question.status === 'replied' ? 'bg-green-100' : 'bg-orange-100'
                    }`}>
                      <svg className={`w-5 h-5 ${
                        question.status === 'replied' ? 'text-green-600' : 'text-orange-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium text-gray-900">{question.student_name || '學生'}</span>
                        <span className="text-sm text-gray-500 ml-2">
                          {new Date(question.created_at).toLocaleString('zh-TW')}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        question.status === 'replied' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {question.status === 'replied' ? '已回覆' : '待回覆'}
                      </span>
                    </div>
                    
                    <p className="text-gray-700 mb-3">{question.content}</p>
                    
                    {/* Display media */}
                    {question.media_urls && question.media_urls.length > 0 && (
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {question.media_urls.map((url, index) => (
                          <img 
                            key={index}
                            src={url} 
                            alt={`附件 ${index + 1}`}
                            className="w-20 h-20 object-cover rounded border cursor-pointer hover:opacity-75"
                            onClick={() => window.open(url, '_blank')}
                          />
                        ))}
                      </div>
                    )}
                    
                    {question.status === 'replied' ? (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                          </svg>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">{question.reply}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              回覆時間：{new Date(question.replied_at).toLocaleString('zh-TW')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          setSelectedQuestion(question);
                          setReplyDialogOpen(true);
                        }}
                      >
                        回覆
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onOpenChange={setReplyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>回覆學生提問</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedQuestion && (
              <>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600 mb-1">學生提問：</p>
                  <p className="text-gray-900">{selectedQuestion.content}</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">回覆內容</label>
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="輸入您的回覆..."
                    rows={5}
                  />
                </div>
                <Button onClick={handleReply} className="w-full">
                  發送回覆
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function OptimizationTab() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  const fetchDatabaseStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/optimize-database`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStats(response.data.stats);
      setRecommendations(response.data.recommendations);
      toast.success('資料庫統計信息已更新');
    } catch (error) {
      console.error('獲取統計信息失敗:', error);
      toast.error(error.response?.data?.detail || '獲取統計信息失敗');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  useEffect(() => {
    fetchDatabaseStats();
  }, []);

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">系統優化</h1>
        <p className="text-gray-600">監控和優化系統性能</p>
      </div>

      <div className="space-y-6">
        {/* 優化功能卡片 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Target className="text-blue-600" size={24} />
            優化功能
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">✓ 圖片自動優化</h3>
              <p className="text-sm text-green-700">
                所有上傳的圖片自動壓縮並轉換為 WebP 格式，大幅減少儲存空間和加載時間
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-800 mb-2">✓ 資料庫索引優化</h3>
              <p className="text-sm text-green-700">
                自動創建和維護資料庫索引，提升查詢速度和系統響應性能
              </p>
            </div>
          </div>
        </div>

        {/* 資料庫統計 */}
        {stats && (
          <>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-xl font-semibold mb-4">資料庫整體統計</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">數據大小</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatBytes(stats.database.dataSize)}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">儲存空間</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {formatBytes(stats.database.storageSize)}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">索引數量</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.database.indexes}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">索引大小</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {formatBytes(stats.database.indexSize)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-xl font-semibold mb-4">各集合統計</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">集合名稱</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">文檔數量</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">數據大小</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">儲存大小</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">平均文檔大小</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">索引數量</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(stats)
                      .filter(([key]) => key !== 'database')
                      .map(([collName, collStats]) => (
                        <tr key={collName} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{collName}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{collStats.count}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(collStats.size)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(collStats.storageSize)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{formatBytes(collStats.avgObjSize)}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{collStats.indexes}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* 優化建議 */}
        {recommendations.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h2 className="text-xl font-semibold mb-4">優化建議</h2>
            <ul className="space-y-2">
              {recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-green-600 mt-1">✓</span>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 刷新按鈕 */}
        <div className="flex justify-end">
          <Button 
            onClick={fetchDatabaseStats}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {loading ? '加載中...' : '刷新統計信息'}
          </Button>
        </div>
      </div>
    </div>
  );
}


function CoachesTab({ user }) {
  const [coaches, setCoaches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone: '',
    password: '',
    gender: 'male',
    coach_name: '',  // 教練姓名
    organization_id: user.organization_id
  });

  useEffect(() => {
    fetchCoaches();
    fetchStudents();
  }, []);

  const fetchCoaches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/coaches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoaches(response.data);
    } catch (error) {
      console.error('獲取教練列表失敗:', error);
      toast.error('獲取教練列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // 篩選學生
      const studentList = response.data.filter(u => u.role === 'student');
      setStudents(studentList);
    } catch (error) {
      console.error('獲取學生列表失敗:', error);
    }
  };

  const handleCreateCoach = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/coaches`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('教練帳戶創建成功！');
      setDialogOpen(false);
      setFormData({
        username: '',
        email: '',
        phone: '',
        password: '',
        gender: 'male',
        coach_name: '',
        organization_id: user.organization_id
      });
      fetchCoaches();
    } catch (error) {
      console.error('創建教練失敗:', error);
      toast.error(error.response?.data?.detail || '創建教練失敗');
    }
  };

  const handleAssignStudents = async () => {
    if (!selectedCoach || selectedStudents.length === 0) {
      toast.error('請選擇學生');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/coaches/${selectedCoach}/assign-students`,
        selectedStudents,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('學生分配成功！');
      setAssignDialogOpen(false);
      setSelectedStudents([]);
      fetchCoaches();
    } catch (error) {
      console.error('分配學生失敗:', error);
      toast.error('分配學生失敗');
    }
  };

  const handleDeleteCoach = async (coachId) => {
    if (!window.confirm('確定要刪除此教練帳戶嗎？')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/coaches/${coachId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('教練刪除成功！');
      fetchCoaches();
    } catch (error) {
      console.error('刪除教練失敗:', error);
      toast.error('刪除教練失敗');
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  if (loading) {
    return <div className="p-8 text-center">加載中...</div>;
  }

  return (
    <div className="p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">教練管理</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              新增教練
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>創建教練帳戶</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCoach} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">教練姓名</label>
                <Input
                  value={formData.coach_name}
                  onChange={(e) => setFormData({ ...formData, coach_name: e.target.value })}
                  placeholder="請輸入教練姓名"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">教練帳號</label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">電子郵件</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">電話號碼</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">密碼</label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">性別</label>
                <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">男</SelectItem>
                    <SelectItem value="female">女</SelectItem>
                    <SelectItem value="other">其他</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                創建教練
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {coaches.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-500">暫無教練</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">教練</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">姓名</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">電子郵件</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">電話</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">負責學生數</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {coaches.map((coach) => (
                  <tr key={coach.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img 
                          src={coach.avatar_url} 
                          alt={coach.username}
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{coach.username}</p>
                          <p className="text-sm text-gray-500">{coach.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{coach.coach_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-900">{coach.email}</td>
                    <td className="px-4 py-3 text-gray-900">{coach.phone}</td>
                    <td className="px-4 py-3 text-gray-900">{coach.managed_students_count || 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button 
                          size="sm"
                          onClick={() => {
                            setSelectedCoach(coach.user_id);
                            setSelectedStudents(coach.managed_students || []);
                            setAssignDialogOpen(true);
                          }}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          分配學生
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleDeleteCoach(coach.user_id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          刪除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 分配學生對話框 */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[600px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>分配學生給教練</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">選擇要分配給此教練的學生：</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {students.map((student) => (
                <label key={student.user_id} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedStudents.includes(student.user_id)}
                    onChange={() => toggleStudentSelection(student.user_id)}
                    className="w-4 h-4"
                  />
                  <img 
                    src={student.avatar_url} 
                    alt={student.username}
                    className="w-8 h-8 rounded-full"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{student.username}</p>
                    <p className="text-sm text-gray-500">{student.student_id}</p>
                    {student.class_location && (
                      <p className="text-sm text-blue-600 mt-1">
                        📍 {student.class_location}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-sm text-gray-600">
                已選擇 {selectedStudents.length} 位學生
              </span>
              <Button onClick={handleAssignStudents} className="bg-blue-600 hover:bg-blue-700">
                確認分配
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

